import {IconButton, isFile, Preview, PreviewAction, Upload} from 'platform/components';
import {Box, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {Lightbox, LightboxAction, LightboxImage, useLightbox} from 'platform/lightbox';

import {useState} from 'react';

import {useUploadFileMutation} from '@dms/api/base';
import {RecordAuditTaskResultFileResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {
  ApiError,
  buildArray,
  DOT_CHARACTER,
  CSV_MIME_TYPE,
  DOC_MIME_TYPE,
  DOCX_MIME_TYPE,
  GIF_MIME_TYPE,
  HEIC_MIME_TYPE,
  JPEG_MIME_TYPE,
  PDF_MIME_TYPE,
  PNG_MIME_TYPE,
  XLS_MIME_TYPE,
  XLSX_MIME_TYPE,
} from 'shared/web';

const ACCEPTED_MIME_TYPES = [
  JPEG_MIME_TYPE,
  PNG_MIME_TYPE,
  GIF_MIME_TYPE,
  HEIC_MIME_TYPE,
  PDF_MIME_TYPE,
  DOC_MIME_TYPE,
  DOCX_MIME_TYPE,
  XLS_MIME_TYPE,
  XLSX_MIME_TYPE,
  CSV_MIME_TYPE,
].join(',');

const LIGHTBOX_ID_PREFIX = 'recordAuditTask';

// Square tile edge for every attachment (image, file, drop-zone) so they line up in
// one row. Upload uses size="default" (no enforced min), so the tiles can be compact.
const TILE_SIZE = 16;
const FILE_NAME_MAX_CHARS = 14;

const truncateFileName = (name: string) =>
  name.length > FILE_NAME_MAX_CHARS ? `${name.slice(0, FILE_NAME_MAX_CHARS)}…` : name;

// The BE reports metadata.fileType as non-"image" right after upload (it probes
// the real content-type only later), which would dump a fresh photo into the
// file-tile bucket until a hard refresh. Fall back to the filename extension so a
// just-uploaded image is recognised immediately.
const IMAGE_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg'];

const hasImageExtension = (filename: string) =>
  IMAGE_FILE_EXTENSIONS.includes(filename.toLowerCase().split('.').pop() ?? '');

const isImageFile = (file: RecordAuditTaskResultFileResponseBody) =>
  file.metadata.fileType === 'image' || hasImageExtension(file.metadata.originalFilename);

interface RecordAuditTaskAttachmentsProps {
  taskId: string;
  // BE-provided current attachments. The component derives its display state
  // from this prop and re-syncs whenever the parent passes a new reference
  // (e.g. after audit refetch on close/reopen/header save).
  initialFiles: RecordAuditTaskResultFileResponseBody[];
  onChange: (fileIds: string[]) => void;
  isDisabled?: boolean;
}

export function RecordAuditTaskAttachments(props: RecordAuditTaskAttachmentsProps) {
  const [lastSeenInitial, setLastSeenInitial] = useState(props.initialFiles);
  const [files, setFiles] = useState<RecordAuditTaskResultFileResponseBody[]>(props.initialFiles);
  if (lastSeenInitial !== props.initialFiles) {
    setLastSeenInitial(props.initialFiles);
    setFiles(props.initialFiles);
  }

  const [uploadFile] = useUploadFileMutation();
  const [lightboxControls, {onOpen}] = useLightbox(`${LIGHTBOX_ID_PREFIX}-${props.taskId}`);

  const imageFiles = files.filter(isImageFile);
  const otherFiles = files.filter((file) => !isImageFile(file));

  const handleRemove = (id: string) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      props.onChange(next.map((f) => f.id));
      return next;
    });
  };

  const lightboxImages: LightboxImage[] = imageFiles.map((file) => ({
    id: file.id,
    url: file.metadata.originalUri,
    title: file.metadata.originalFilename,
    fileName: file.metadata.originalFilename,
    actions: buildArray<LightboxAction>().whenNot(props.isDisabled, {
      id: 'delete',
      icon: 'action/delete',
      onClick: () => handleRemove(file.id),
    }),
  }));

  return (
    <VStack spacing={2}>
      <Show when={files.length > 0 || !props.isDisabled}>
        <VStack spacing={1}>
          <Text size="xSmall" color="tertiary">
            {`${i18n.t('entity.document.labels.attachments').toUpperCase()}${
              files.length > 0 ? ` ${DOT_CHARACTER} ${files.length}` : ''
            }`}
          </Text>
          <HStack spacing={2} wrap>
            {imageFiles.map((file, index) => (
              <Box
                key={file.id}
                width={TILE_SIZE}
                height={TILE_SIZE}
                overflow="hidden"
                borderRadius="small"
                position="relative"
              >
                <Preview
                  url={file.metadata.originalUri}
                  // Load the raw original, not the resize-CDN derivative: a freshly
                  // uploaded file has no derivative yet, so makeStorageUrl would 404
                  // until it is generated (the "blank until hard refresh" bug). Same
                  // approach as platform UploadImage.
                  makeUrl={() => file.metadata.originalUri}
                  width="100%"
                  height="100%"
                  fit="cover"
                  alt={file.metadata.originalFilename}
                  actions={buildArray<PreviewAction>()
                    .add({icon: 'action/visibility', onClick: () => onOpen(index)})
                    .whenNot(props.isDisabled, {
                      icon: 'action/delete',
                      onClick: () => handleRemove(file.id),
                    })}
                  data-testid={`recordAuditAttachment-image-${file.id}`}
                />
              </Box>
            ))}
            {otherFiles.map((file) => (
              <FileTile
                key={file.id}
                file={file}
                onRemove={() => handleRemove(file.id)}
                isDisabled={props.isDisabled}
              />
            ))}
            <Show when={!props.isDisabled}>
              <Box width={TILE_SIZE} height={TILE_SIZE}>
                <Upload<'card'>
                  type="card"
                  size="default"
                  accept={ACCEPTED_MIME_TYPES}
                  shouldValidateAccept
                  errorIcon="navigation/cancel"
                  uploadIcon="file/upload"
                  uploadText={i18n.t('page.checklistAudits.form.attachments.add')}
                  customRequest={({file, onSuccess, onError}) => {
                    if (!isFile(file)) {
                      return;
                    }
                    uploadFile({file})
                      .unwrap()
                      .then((data) => {
                        const uploaded: RecordAuditTaskResultFileResponseBody = {
                          id: data.fileId,
                          metadata: {
                            originalFilename: data.file.name,
                            fileType: detectFileType(file.type),
                            originalUri: data.fileUri,
                            dimensions: null,
                          },
                        };
                        setFiles((prev) => {
                          const next = [...prev, uploaded];
                          props.onChange(next.map((f) => f.id));
                          return next;
                        });
                        onSuccess?.(data);
                      })
                      .catch((error: ApiError) => {
                        handleApiError(error);
                        onError?.(
                          new Error(i18n.t('page.checklistAudits.form.attachments.uploadFailed'))
                        );
                      });
                  }}
                />
              </Box>
            </Show>
          </HStack>
        </VStack>
      </Show>
      <Lightbox
        data-testid={`recordAuditAttachment-lightbox-${props.taskId}`}
        controls={lightboxControls}
        images={lightboxImages}
      />
    </VStack>
  );
}

interface FileTileProps {
  file: RecordAuditTaskResultFileResponseBody;
  onRemove: () => void;
  isDisabled?: boolean;
}

// Non-image attachment shown as a square tile alongside the photos: doc glyph +
// truncated, clickable filename, with a delete affordance in the corner.
function FileTile(props: FileTileProps) {
  return (
    <Box
      width={TILE_SIZE}
      height={TILE_SIZE}
      borderRadius="small"
      border="1px solid"
      borderColor="palettes.neutral.30.100"
      backgroundColor="palettes.neutral.10.100"
      position="relative"
    >
      <VStack height="100%" align="center" justify="center" spacing={1}>
        <Icon value="action/description" size={8} color="palettes.neutral.70.100" />
        <Link
          size="small"
          title={truncateFileName(props.file.metadata.originalFilename)}
          href={props.file.metadata.originalUri}
          target="_blank"
        />
      </VStack>
      <Show when={!props.isDisabled}>
        <Box position="absolute" top={0} right={0}>
          <IconButton icon="action/delete" size="small" onClick={props.onRemove} />
        </Box>
      </Show>
    </Box>
  );
}

const detectFileType = (mime: string): 'image' | 'video' | 'unknown' => {
  if (mime.startsWith('image/')) {
    return 'image';
  }
  if (mime.startsWith('video/')) {
    return 'video';
  }
  return 'unknown';
};
